import {
  LAST24H_STATUS_ROWS,
  ROLE_COLUMNS,
  type NotificationRole,
  type NotificationSpecifics,
} from '~/helpers/notificationPrefs';
import {
  aggregateSelectState,
  summaryParentColumnValues,
  summarySectionValues,
  summaryStatusIncludeColumnValues,
} from '~/helpers/notificationPrefsSelectAll';
import { PrefCheckbox } from './PrefCheckbox';
import { ScrollablePrefsGrid } from './ScrollablePrefsGrid';
import { SectionSelectAll, toCheckboxState } from './SectionSelectAll';
import { SendTimeSelect } from './SendTimeSelect';

type Props = {
  jobSummaries: NotificationSpecifics['jobSummaries'];
  showStatusInclude: boolean;
  disabled?: boolean;
  onToggleRole: (
    window: 'next24Hours' | 'last24Hours',
    role: NotificationRole,
    next: boolean
  ) => void;
  onSendTime: (window: 'next24Hours' | 'last24Hours', next: string) => void;
  onToggleStatusInclude: (
    status: keyof NotificationSpecifics['jobSummaries']['last24Hours']['statusChangesToInclude'],
    role: NotificationRole,
    next: boolean
  ) => void;
  onSectionSelectAll: (checked: boolean) => void;
  onParentColumnSelectAll: (role: NotificationRole, checked: boolean) => void;
  onStatusIncludeColumnSelectAll: (
    role: NotificationRole,
    checked: boolean
  ) => void;
};

/**
 * Shared 5-column track: label | Assigned | Owner | Included | Send time.
 * Last 24h card + nested matrix use `subgrid` so checkboxes stay under the
 * same role headers; nested rows leave the Send time column empty.
 */
const summariesGridClass =
  'grid w-fit grid-cols-[minmax(9rem,auto)_repeat(3,minmax(5.5rem,auto))_minmax(7rem,auto)] items-center gap-x-6 gap-y-3';

const roleHeaderClass =
  'max-w-[7rem] text-center text-xs font-semibold leading-snug text-primary';

export const SummariesSpecifics = ({
  jobSummaries,
  showStatusInclude,
  disabled,
  onToggleRole,
  onSendTime,
  onToggleStatusInclude,
  onSectionSelectAll,
  onParentColumnSelectAll,
  onStatusIncludeColumnSelectAll,
}: Props) => {
  const sectionState = aggregateSelectState(summarySectionValues(jobSummaries));

  return (
    <div className='flex min-w-0 flex-col gap-3'>
      <h3 className='font-semibold text-primary'>Job Summaries</h3>
      <SectionSelectAll
        state={sectionState}
        disabled={disabled}
        aria-label='Job Summaries Select All'
        onChange={onSectionSelectAll}
      />

      <ScrollablePrefsGrid className={summariesGridClass}>
        <div />
        {ROLE_COLUMNS.map((col) => (
          <div key={col.key} className={roleHeaderClass}>
            {col.label}
          </div>
        ))}
        <div className='text-center text-xs font-semibold text-primary'>
          Send time
        </div>

        <div className='text-sm text-primary'>Select All</div>
        {ROLE_COLUMNS.map((col) => {
          const colState = aggregateSelectState(
            summaryParentColumnValues(jobSummaries, col.key)
          );
          return (
            <PrefCheckbox
              key={col.key}
              state={toCheckboxState(colState)}
              aria-label={`Job Summaries Select All — ${col.label}`}
              disabled={disabled}
              onChange={(next) => onParentColumnSelectAll(col.key, next)}
            />
          );
        })}
        {/* No Select All for Send time column */}
        <div aria-hidden='true' />

        <div className='text-sm text-primary'>Next 24 hours</div>
        {ROLE_COLUMNS.map((col) => (
          <PrefCheckbox
            key={col.key}
            aria-label={`Next 24 hours — ${col.label}`}
            checked={jobSummaries.next24Hours[col.key]}
            disabled={disabled}
            onChange={(next) => onToggleRole('next24Hours', col.key, next)}
          />
        ))}
        <SendTimeSelect
          aria-label='Next 24 hours send time'
          value={jobSummaries.next24Hours.sendTime}
          disabled={disabled}
          onChange={(next) => onSendTime('next24Hours', next)}
        />

        {/*
          Last 24h + nested panel share parent column tracks via subgrid.
          No horizontal padding — padding would inset content off the tracks.
        */}
        <div
          className={
            showStatusInclude
              ? 'col-span-5 grid grid-cols-subgrid gap-y-3 rounded-md border border-outline-variant py-3'
              : 'col-span-5 grid grid-cols-subgrid gap-y-3'
          }
        >
          <div className='text-sm text-primary'>Last 24 hours</div>
          {ROLE_COLUMNS.map((col) => (
            <PrefCheckbox
              key={col.key}
              aria-label={`Last 24 hours — ${col.label}`}
              checked={jobSummaries.last24Hours[col.key]}
              disabled={disabled}
              onChange={(next) => onToggleRole('last24Hours', col.key, next)}
            />
          ))}
          <SendTimeSelect
            aria-label='Last 24 hours send time'
            value={jobSummaries.last24Hours.sendTime}
            disabled={disabled}
            onChange={(next) => onSendTime('last24Hours', next)}
          />

          {showStatusInclude && (
            <>
              <div className='col-span-4 grid grid-cols-subgrid gap-y-2 rounded-md border border-outline-variant bg-shade-10 py-3'>
                <div className='col-span-4 text-sm font-semibold text-primary'>
                  Status changes to include
                </div>
                {/* Column Select All only — no section Select All under nested subheading */}
                <div className='text-sm text-primary'>Select All</div>
                {ROLE_COLUMNS.map((col) => {
                  const colState = aggregateSelectState(
                    summaryStatusIncludeColumnValues(jobSummaries, col.key)
                  );
                  return (
                    <PrefCheckbox
                      key={col.key}
                      state={toCheckboxState(colState)}
                      aria-label={`Status changes to include Select All — ${col.label}`}
                      disabled={disabled}
                      onChange={(next) =>
                        onStatusIncludeColumnSelectAll(col.key, next)
                      }
                    />
                  );
                })}
                {LAST24H_STATUS_ROWS.map((row) => (
                  <div key={row.key} className='contents'>
                    <div className='text-sm text-primary'>
                      <span>{row.label}</span>
                      {row.hint ? (
                        <span className='mt-0.5 block text-xs italic text-tertiary'>
                          {row.hint}
                        </span>
                      ) : null}
                    </div>
                    {ROLE_COLUMNS.map((col) => (
                      <PrefCheckbox
                        key={col.key}
                        aria-label={`Status changes to include — ${row.label} — ${col.label}`}
                        checked={
                          jobSummaries.last24Hours.statusChangesToInclude[
                            row.key
                          ][col.key]
                        }
                        disabled={disabled}
                        onChange={(next) =>
                          onToggleStatusInclude(row.key, col.key, next)
                        }
                      />
                    ))}
                  </div>
                ))}
              </div>
              {/* Send time gutter for nested block */}
              <div aria-hidden='true' />
            </>
          )}
        </div>
      </ScrollablePrefsGrid>
    </div>
  );
};
